import type {
  CreateShortlistInput,
  PublicShortlistReviewCandidate,
  PublicShortlistReviewDecisionInput,
  PublicShortlistReviewResult,
  PublicShortlistReviewSession,
  PublicShortlistSummary,
  ShortlistCandidateSummary,
  ShortlistDetail,
  ShortlistFeedbackEntry,
  ShortlistFeedbackSummary,
  ShortlistListResult,
  UpdateShortlistInput,
} from '@tabbio/types';
import { api } from './api';
import { getLibraryResponseError } from './library-api-error';

export interface FetchShortlistsParams {
  q?: string;
  cursor?: string;
  limit?: number;
}

function buildShortlistsQuery(params: FetchShortlistsParams = {}) {
  const query = new URLSearchParams();
  if (params.q?.trim()) query.set('q', params.q.trim());
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.limit) query.set('limit', String(params.limit));
  return query.size ? `?${query.toString()}` : '';
}

function withSessionToken(sessionToken?: string | null) {
  return sessionToken
    ? {
        requiresAuth: false as const,
        headers: {
          'x-shortlist-session-token': sessionToken,
        },
      }
    : { requiresAuth: false as const };
}

export async function fetchShortlists(
  params: FetchShortlistsParams = {},
): Promise<ShortlistListResult> {
  const response = await api.get<ShortlistListResult>(
    `/api/shortlists${buildShortlistsQuery(params)}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (
    response.data ?? {
      shortlists: [],
      total: 0,
      nextCursor: null,
    }
  );
}

export async function fetchShortlistDetail(shortlistId: string): Promise<ShortlistDetail> {
  const response = await api.get<ShortlistDetail>(`/api/shortlists/${shortlistId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Shortlist not found');
  return response.data;
}

export async function createShortlist(
  input: CreateShortlistInput,
): Promise<ShortlistDetail> {
  const response = await api.post<ShortlistDetail>('/api/shortlists', input);
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Shortlist creation returned no data');
  return response.data;
}

export async function updateShortlist(
  shortlistId: string,
  input: UpdateShortlistInput,
): Promise<ShortlistDetail> {
  const response = await api.patch<ShortlistDetail>(
    `/api/shortlists/${shortlistId}`,
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Shortlist update returned no data');
  return response.data;
}

export async function deleteShortlist(shortlistId: string): Promise<void> {
  const response = await api.delete(`/api/shortlists/${shortlistId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function fetchShortlistCandidates(
  shortlistId: string,
): Promise<ShortlistCandidateSummary[]> {
  const response = await api.get<ShortlistCandidateSummary[]>(
    `/api/shortlists/${shortlistId}/candidates`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return response.data ?? [];
}

export async function setShortlistCandidates(
  shortlistId: string,
  candidateIds: string[],
): Promise<ShortlistDetail> {
  const response = await api.put<ShortlistDetail>(
    `/api/shortlists/${shortlistId}/candidates`,
    { candidateIds },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Shortlist candidates update returned no data');
  return response.data;
}

export async function removeShortlistCandidate(
  shortlistId: string,
  candidateId: string,
): Promise<void> {
  const response = await api.delete(
    `/api/shortlists/${shortlistId}/candidates/${candidateId}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function fetchShortlistFeedback(
  shortlistId: string,
): Promise<ShortlistFeedbackEntry[]> {
  const response = await api.get<ShortlistFeedbackEntry[]>(
    `/api/shortlists/${shortlistId}/feedback`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return response.data ?? [];
}

export async function fetchShortlistFeedbackSummary(
  shortlistId: string,
): Promise<ShortlistFeedbackSummary> {
  const response = await api.get<ShortlistFeedbackSummary>(
    `/api/shortlists/${shortlistId}/feedback-summary`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (
    response.data ?? {
      reviewersCount: 0,
      reviewsCount: 0,
      yesCount: 0,
      noCount: 0,
    }
  );
}

export async function fetchPublicShortlist(
  slug: string,
): Promise<PublicShortlistSummary> {
  const response = await api.get<PublicShortlistSummary>(
    `/api/public/shortlists/${slug}`,
    { requiresAuth: false },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Shortlist not found');
  return response.data;
}

export async function fetchPublicShortlistReviewCandidates(
  slug: string,
): Promise<PublicShortlistReviewCandidate[]> {
  const response = await api.get<PublicShortlistReviewCandidate[]>(
    `/api/public/shortlists/${slug}/review-candidates`,
    { requiresAuth: false },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return response.data ?? [];
}

export async function createPublicShortlistReviewSession(
  slug: string,
  reviewerName: string,
): Promise<PublicShortlistReviewSession> {
  const response = await api.post<PublicShortlistReviewSession>(
    `/api/public/shortlists/${slug}/review-sessions`,
    { reviewerName },
    { requiresAuth: false },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Review session creation returned no data');
  return response.data;
}

export async function fetchPublicShortlistReviewSession(
  slug: string,
  sessionId: string,
  sessionToken: string,
): Promise<PublicShortlistReviewSession> {
  const response = await api.get<PublicShortlistReviewSession>(
    `/api/public/shortlists/${slug}/review-sessions/${sessionId}`,
    withSessionToken(sessionToken),
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Review session not found');
  return response.data;
}

export async function updatePublicShortlistReviewDecision(
  slug: string,
  sessionId: string,
  sessionToken: string,
  candidateId: string,
  input: {
    decision?: PublicShortlistReviewDecisionInput['decision'] | 'pending';
    note?: string;
  },
): Promise<PublicShortlistReviewSession> {
  const response = await api.patch<PublicShortlistReviewSession>(
    `/api/public/shortlists/${slug}/review-sessions/${sessionId}/candidates/${candidateId}`,
    input,
    withSessionToken(sessionToken),
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Review update returned no data');
  return response.data;
}

export async function submitPublicShortlistReview(
  slug: string,
  sessionId: string,
  sessionToken: string,
): Promise<PublicShortlistReviewResult> {
  const response = await api.post<PublicShortlistReviewResult>(
    `/api/public/shortlists/${slug}/review-sessions/${sessionId}/submit`,
    {},
    withSessionToken(sessionToken),
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Review submit returned no data');
  return response.data;
}
